import { Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { CheckCircle, Cancel, Business } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getDomainIcon } from './domain-icons';
import { fetchApi } from '@/api/fetchApi';

export interface DomainInfoPanelProps {
  domainId: string;
  showEntities?: boolean;
  showPermissions?: boolean;
  showDependencies?: boolean;
}

const statusColor = (status: string): 'success' | 'warning' | 'default' | 'error' => {
  switch (status) {
    case 'Active': return 'success';
    case 'Trial': return 'warning';
    case 'Suspended': return 'error';
    case 'Expired': return 'default';
    default: return 'default';
  }
};

export function DomainInfoPanel({ domainId, showEntities = true, showPermissions = false, showDependencies = true }: DomainInfoPanelProps) {
  const { data: domain, isLoading: loadingDomain } = useQuery({
    queryKey: ['domain', domainId],
    queryFn: () => fetchApi(`/domains/${domainId}`),
    staleTime: 5 * 60 * 1000,
    enabled: !!domainId,
  });

  const { data: deps, isLoading: loadingDeps } = useQuery({
    queryKey: ['domain-deps', domainId],
    queryFn: () => fetchApi(`/domains/${domainId}/dependencies`),
    staleTime: 5 * 60 * 1000,
    enabled: !!domainId && showDependencies,
  });

  const { data: tenants, isLoading: loadingTenants } = useQuery({
    queryKey: ['domain-tenants', domainId],
    queryFn: () => fetchApi(`/domains/${domainId}/tenants`),
    staleTime: 5 * 60 * 1000,
    enabled: !!domainId,
  });

  if (loadingDomain || loadingDeps || loadingTenants) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!domain) {
    return <Alert severity="error">Domain not found.</Alert>;
  }

  const d = domain as {
    domainId: string;
    name: string;
    description: string;
    level: string;
    billingTier: string;
    status: string;
    version: string;
    entities?: string[];
    permissions?: string[];
    regulatoryTouchpoints?: string[];
    dependencies?: string[];
    parentId?: string;
  };

  const depData = deps as { valid: boolean; missing: string[] } | undefined;
  const tenantList = (Array.isArray(tenants) ? tenants : []) as Array<{
    tenantId: string;
    tenantName?: string;
    tradingName?: string;
    status: string;
    grantedAt: string;
    expiresAt: string | null;
  }>;

  return (
    <Card variant="outlined">
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {getDomainIcon(domainId)}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{d.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {d.domainId}
            </Typography>
          </Box>
          <Chip label={d.level} size="small" variant="outlined" />
          <Chip label={d.billingTier} size="small" color="primary" variant="outlined" />
          <Chip label={d.status} size="small" color={d.status === 'Active' ? 'success' : 'default'} />
        </Box>

        {/* Description */}
        {d.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{d.description}</Typography>
        )}

        {/* Key Info Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 2 }}>
          {[
            { label: 'Parent', value: d.parentId || 'Root' },
            { label: 'Version', value: d.version || '1.0.0' },
            { label: 'Entities', value: `${d.entities?.length ?? 0}` },
            { label: 'Permissions', value: `${d.permissions?.length ?? 0}` },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
            </Box>
          ))}
        </Box>

        {/* Regulatory Touchpoints */}
        {d.regulatoryTouchpoints && d.regulatoryTouchpoints.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Regulations
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {d.regulatoryTouchpoints.map((r) => (
                <Chip key={r} label={r} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {/* Entities */}
        {showEntities && d.entities && d.entities.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Entities
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {d.entities.map((e) => (
                <Chip key={e} label={e} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {/* Permissions */}
        {showPermissions && d.permissions && d.permissions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Permissions
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {d.permissions.map((p) => (
                <Chip key={p} label={p} size="small" variant="outlined" color="info" />
              ))}
            </Box>
          </Box>
        )}

        {/* Dependencies */}
        {showDependencies && depData && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
              Dependencies
            </Typography>
            {!depData.missing || depData.missing.length === 0 ? (
              <Typography variant="body2" color="success.main">All dependencies met</Typography>
            ) : (
              <Box>
                {depData.missing.map((m) => (
                  <Box key={m} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Cancel fontSize="small" color="error" />
                    <Typography variant="body2">{m} — unmet</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Tenant Access Table */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Business fontSize="small" color="action" />
            <Typography variant="subtitle2">
              Tenants with access ({tenantList.length})
            </Typography>
          </Box>
          {tenantList.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Granted</TableCell>
                    <TableCell>Expires</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenantList.map((t) => (
                    <TableRow key={t.tenantId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {t.tradingName || t.tenantName || t.tenantId}
                        </Typography>
                        {(t.tradingName || t.tenantName) && t.tenantId !== (t.tradingName || t.tenantName) && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                            {t.tenantId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={t.status} size="small" color={statusColor(t.status)} sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{t.grantedAt ? new Date(t.grantedAt).toLocaleDateString() : '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{t.expiresAt ? new Date(t.expiresAt).toLocaleDateString() : 'Perpetual'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No tenants have access to this domain.
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
